import {
  BonusInstanceStatus,
  BonusProductType,
  BonusType,
  FreeSpinStatus,
  FreeSpinSubType,
} from '@atlas/bonuses';

/**
 * UI groups (chips) shown on the bonus list page (order matters)
 */
export const UI_GROUP = {
  ACTIVATED: 1,
  AVAILABLE: 0,
  FINISHED: 3,
  UNLOCKED: 2,
};

export const DEPOSIT_LIKE_TYPES = new Set([BonusType.DEPOSIT, BonusType.RELOAD_DEPOSIT]);

export const BONUS_FILTERS = {
  ACTIVATED: 'ACTIVATED',
  ALL: 'ALL',
  AVAILABLE: 'AVAILABLE',
  FINISHED: 'FINISHED',
  UNLOCKED: 'UNLOCKED',
};

export const BONUS_FILTER_CHIPS = [
  { id: BONUS_FILTERS.ALL, message: 'bonuses_all' },
  { id: BONUS_FILTERS.ACTIVATED, message: 'bonuses_activated' },
  { id: BONUS_FILTERS.AVAILABLE, message: 'bonuses_avaliable' },
  { id: BONUS_FILTERS.FINISHED, message: 'bonuses_finished' },
];

const FILTER_TO_UI_GROUP = {
  [BONUS_FILTERS.AVAILABLE]: UI_GROUP.AVAILABLE,
  [BONUS_FILTERS.ACTIVATED]: UI_GROUP.ACTIVATED,
  [BONUS_FILTERS.FINISHED]: UI_GROUP.FINISHED,
};

/**
 * Templates returned by `bonus:search` don't carry a user-specific status (they're
 * not bound to a client yet). Anything with a `status` is a bonus instance
 */
export const isBonusTemplate = row => row.status == null || row.status === '';

export const getBonusUiGroup = row => {
  if (isBonusTemplate(row)) {
    return UI_GROUP.AVAILABLE;
  }

  switch (row.status) {
    case BonusInstanceStatus.ACTIVATION_READY:
      return UI_GROUP.AVAILABLE;
    case BonusInstanceStatus.PENDING:
    case BonusInstanceStatus.PENDING_ACTIVATION:
    case BonusInstanceStatus.IN_PROGRESS:
      return UI_GROUP.ACTIVATED;
    default:
      return UI_GROUP.FINISHED;
  }
};

export const getFreespinUiGroup = freespin => {
  switch (freespin.status) {
    case FreeSpinStatus.ACTIVATION_READY:
      return UI_GROUP.AVAILABLE;
    case FreeSpinStatus.PENDING_ACTIVATION:
      return UI_GROUP.UNLOCKED;
    case FreeSpinStatus.ACTIVATED:
      return UI_GROUP.ACTIVATED;
    default:
      return UI_GROUP.FINISHED;
  }
};

/**
 * Used to sort cards within a UI group by the most relevant end date (newest first)
 */
export const getBonusSortEndMs = row => {
  if (row.endTime != null) return row.endTime;
  if (row.expirationTime != null) return row.expirationTime;
  if (row.wagerStartTime && row.timeToWager) return row.wagerStartTime + row.timeToWager;
  return 0;
};

export const getFreespinSortEndMs = freespin => {
  const { createTime, expirationTime, freeSpin, freeSpinSubType } = freespin;

  if (freeSpinSubType === FreeSpinSubType.DEPOSIT) {
    return expirationTime > 0 ? expirationTime : 0;
  }

  if (Number.isFinite(createTime) && Number.isFinite(freeSpin?.timeToUse)) {
    return createTime + freeSpin.timeToUse;
  }
  return 0;
};

export const filterByChip = (items, chipId, hideFinishedFromAll) => {
  if (chipId === BONUS_FILTERS.ALL) {
    return hideFinishedFromAll ? items.filter(i => i.uiGroup !== UI_GROUP.FINISHED) : items;
  }
  const uiGroup = FILTER_TO_UI_GROUP[chipId];
  return uiGroup == null ? items : items.filter(i => i.uiGroup === uiGroup);
};

const compareKindAndDate = (a, b) => {
  if (a.kind !== b.kind) return a.kind === 'bonus' ? -1 : 1;
  return (b.sortEndMs || 0) - (a.sortEndMs || 0);
};

/**
 * Sort order inside the feed:
 * 1. UI group (Available -> Activated -> Unlocked -> Finished)
 * 2. Bonuses before freespins (within the same group)
 * 3. Newest end date first
 */
export const sortFeedItems = items =>
  [...items].sort((a, b) => {
    if (a.uiGroup !== b.uiGroup) return a.uiGroup - b.uiGroup;
    return compareKindAndDate(a, b);
  });

export const isSportBonus = row => row.productType === BonusProductType.SPORTSBOOK;
